import React, { useEffect, useRef, useState } from "react";
import { Pause, Play, Square } from "lucide-react";

/**
 * BreathPlayer — a quiet, minimal practice player.
 *
 * Implementation notes:
 *   • The clock (phase index + elapsed within phase + total elapsed) is
 *     held in refs, NOT state. The rAF tick advances the refs, handles
 *     phase transitions imperatively, and calls a single setState() per
 *     frame to trigger a re-render.
 *   • This keeps React's state-updater contract pure. An earlier version
 *     advanced the phase index from inside a setPhaseElapsed functional
 *     updater; under React 18 StrictMode's double-invocation of
 *     functional updaters, that caused every other phase to be skipped
 *     (the Physiological Sigh bug).
 *
 * Props:
 *   phases: [{ name, seconds, scale }]
 *   totalSeconds: number
 *   intention?: string
 *   onComplete: () => void
 *   onEnd: () => void
 *   testid?: string
 */
export default function BreathPlayer({
  phases,
  totalSeconds,
  intention,
  onComplete,
  onEnd,
  testid = "breath-player",
}) {
  const [running, setRunning] = useState(true);
  const [, forceRender] = useState(0); // bumps to trigger re-render at frame end

  const phaseIndexRef = useRef(0);
  const phaseElapsedRef = useRef(0);
  const totalElapsedRef = useRef(0);
  const lastTsRef = useRef(null);
  const rafRef = useRef(null);
  const completedRef = useRef(false);

  useEffect(() => {
    if (!running) {
      lastTsRef.current = null;
      cancelAnimationFrame(rafRef.current);
      return;
    }
    const tick = (ts) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;

      // Advance the phase clock. Handles multiple phase transitions in a
      // single frame if the tab was backgrounded and the delta is large.
      phaseElapsedRef.current += dt;
      let currentSeconds = phases[phaseIndexRef.current].seconds;
      while (phaseElapsedRef.current >= currentSeconds) {
        phaseElapsedRef.current -= currentSeconds;
        phaseIndexRef.current = (phaseIndexRef.current + 1) % phases.length;
        currentSeconds = phases[phaseIndexRef.current].seconds;
      }

      // Advance the session clock.
      totalElapsedRef.current += dt;
      if (totalElapsedRef.current >= totalSeconds) {
        totalElapsedRef.current = totalSeconds;
        if (!completedRef.current) {
          completedRef.current = true;
          setRunning(false);
          // Defer so the parent unmounts us cleanly.
          setTimeout(() => onComplete?.(), 0);
        }
        forceRender((t) => t + 1);
        return;
      }

      forceRender((t) => t + 1);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // phases/totalSeconds/onComplete are captured by closure; they don't
    // change for the lifetime of a single session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const currentPhase = phases[phaseIndexRef.current];
  const nextPhase = phases[(phaseIndexRef.current + 1) % phases.length];
  const phaseElapsed = phaseElapsedRef.current;
  const phaseSecondsLeft = Math.max(
    0,
    Math.ceil(currentPhase.seconds - phaseElapsed),
  );

  const totalLeft = Math.max(0, Math.ceil(totalSeconds - totalElapsedRef.current));
  const mm = String(Math.floor(totalLeft / 60)).padStart(2, "0");
  const ss = String(totalLeft % 60).padStart(2, "0");

  // Interpolate the orb size across the current phase for smooth motion.
  const t = Math.min(1, phaseElapsed / currentPhase.seconds);
  const orbScale = currentPhase.scale + (nextPhase.scale - currentPhase.scale) * t;
  const orbSize = 220;
  const size = Math.round(orbSize * orbScale);

  return (
    <div className="flex flex-col items-center" data-testid={testid}>
      <div
        className="relative flex items-center justify-center"
        style={{ height: orbSize + 60, width: "100%" }}
      >
        <div
          aria-hidden
          className="absolute rounded-full pointer-events-none"
          style={{
            width: size + 90,
            height: size + 90,
            background:
              "radial-gradient(circle at 50% 50%, rgba(225,146,113,0.18), transparent 65%)",
          }}
        />
        <div
          aria-hidden
          className="rounded-full"
          style={{
            width: size,
            height: size,
            background:
              "radial-gradient(circle at 35% 30%, rgba(255,214,170,0.95), rgba(210,120,80,0.9) 55%, rgba(140,80,55,0.85) 100%)",
            boxShadow:
              "0 10px 32px -10px rgba(140,80,55,0.55), inset 0 -12px 24px rgba(70,32,20,0.35)",
          }}
          data-testid="breath-orb"
        />
      </div>

      <div
        className="mt-2 text-[11px] uppercase tracking-[0.32em] text-[hsl(var(--copper))]"
        data-testid="breath-phase-label"
      >
        {currentPhase.name}
      </div>
      <div
        className="font-display text-5xl md:text-6xl mt-2 tabular-nums text-[hsl(var(--fg))]"
        data-testid="breath-phase-count"
        aria-live="polite"
      >
        {phaseSecondsLeft}
      </div>

      {intention && (
        <p
          className="mt-6 font-display italic text-xl md:text-2xl text-[hsl(var(--clay))]"
          data-testid="breath-intention"
        >
          {intention}
        </p>
      )}

      <div className="mt-8 flex items-center gap-3 flex-wrap justify-center">
        <button
          type="button"
          onClick={() => setRunning((r) => !r)}
          data-testid={running ? "breath-pause" : "breath-resume"}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border hairline bg-[hsl(var(--paper))] text-[hsl(var(--fg))] hover:border-[hsl(var(--copper-light))] transition-all"
        >
          {running ? <Pause size={14} /> : <Play size={14} />}
          <span className="text-[11px] uppercase tracking-[0.24em]">
            {running ? "Pause" : "Resume"}
          </span>
        </button>
        <button
          type="button"
          onClick={() => onEnd?.()}
          data-testid="breath-end"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--copper))] transition-colors"
        >
          <Square size={13} />
          <span className="text-[11px] uppercase tracking-[0.24em]">End</span>
        </button>
      </div>

      <div
        className="mt-6 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]"
        data-testid="breath-total-timer"
        aria-live="polite"
      >
        {mm}:{ss} remaining
      </div>
    </div>
  );
}
